/**
 * `realtimeKeyMint.ts` — Campfire's ephemeral realtime key mint.
 *
 * Port of Unity's `MintKeyRoutine` (`AIRealtimeTransportModelRTC.cs`). Kept
 * out of the transport so the request contract sits beside the Responses auth
 * it shares.
 *
 * The contract:
 *
 *   POST https://{aisvc_host}/v1/realtime/client_secrets
 *   Authorization: Bearer {cognito id token}
 *   -> 200 { value, expires_at, session: { … } }   // OpenAI client-secret shape
 *   -> 401                                          // not signed in / expired
 *
 * ## Why this matters more than it looks
 *
 * The response is a **new-style client secret** (`/v1/realtime/client_secrets`
 * shape), and the minted key is accepted by
 * `POST https://api.openai.com/v1/realtime/calls` — verified. That is what lets
 * the WebRTC transport do its SDP exchange BROWSER-DIRECT, with no proxy and no
 * server-side OpenAI key, on any origin:
 *
 *   - the Campfire proxy allow-lists the caller's `Origin` and the
 *     `Authorization` header;
 *   - `api.openai.com/v1/realtime/calls` answers `Access-Control-Allow-Origin: *`.
 *
 * ⚠ `session.model` comes back EMPTY, so the mint does not pin a model — the
 * caller's `?model=` on the calls URL is what chooses it. Do not assume a
 * minted key locks the session to anything; its `session` block is OpenAI's own
 * boilerplate (voice `alloy`, stock instructions) and the first `session.update`
 * replaces all of it.
 *
 * **No shared app key.** This used to be a separate mint service authenticated
 * by a committed `X-App-Key`. It now rides the same Cognito-authenticated proxy
 * as every other AI call, so the credential is the signed-in user's and there
 * is no long-lived secret in the bundle. The proxy holds the provider key and
 * mints against OpenAI itself. Deliberately routed through `aiProxyFetch` so
 * the mint cannot drift from the auth the Responses path uses.
 */

import { aiProxyFetch } from "@ai/subagents/aiResponsesUtility";

/** Unity's `keyRequest.timeout = 10`. */
const K_MINT_TIMEOUT_MS = 10_000;

const K_MINT_PATH = "/v1/realtime/client_secrets";

/**
 * Mints one ephemeral realtime key, or returns null.
 *
 * Never throws: every caller is on a connect path where the only useful
 * response to a failure is to try the next option. That includes the
 * not-signed-in case, which `aiProxyFetch` raises as a throw.
 */
export async function mintRealtimeEphemeralKeyAsync(): Promise<string | null> {
  try {
    const response = await aiProxyFetch(
      K_MINT_PATH,
      { method: "POST" },
      K_MINT_TIMEOUT_MS,
    );
    if (!response.ok) {
      console.warn(`[realtimeKeyMint] mint refused (${response.status})`);
      return null;
    }
    const body = (await response.json()) as Record<string, unknown>;
    return extractEphemeralKey(body);
  } catch (error) {
    console.warn("[realtimeKeyMint] mint failed:", error);
    return null;
  }
}

/**
 * Port of `OpenAIRealtimeUtils.TryExtractEphemeralKey`.
 *
 * Three accepted shapes because the mint service has carried all of them:
 * `client_secret.value` (the old `/v1/realtime/sessions` response), and the
 * bare `value` / `key` the current service returns.
 */
export function extractEphemeralKey(body: Record<string, unknown>): string | null {
  const clientSecret = body.client_secret;
  if (typeof clientSecret === "object" && clientSecret !== null) {
    const value = (clientSecret as Record<string, unknown>).value;
    if (typeof value === "string" && value) return value;
  }
  if (typeof body.value === "string" && body.value) return body.value;
  if (typeof body.key === "string" && body.key) return body.key;
  return null;
}
